import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, Instagram, Facebook, Linkedin, Plug, ShieldAlert, ExternalLink, Trash2, CheckCircle2 } from "lucide-react";
import { useConfirm } from "../components/ConfirmProvider";
import SocialConnectOptions from "../components/SocialConnectOptions";
import { useAuth } from "../context/AuthContext";

const PROVIDERS = [
  { key: "meta", label: "Instagram + Facebook", tag: "via Meta Graph API", icon: Instagram, tint: "from-orange-500/30 to-red-500/20 border-orange-500/30", envLabel: "META_APP_ID / META_APP_SECRET", channels: [Instagram, Facebook] },
  { key: "linkedin", label: "LinkedIn Pages", tag: "via LinkedIn Marketing API", icon: Linkedin, tint: "from-blue-500/30 to-sky-500/20 border-blue-500/30", envLabel: "LINKEDIN_CLIENT_ID / LINKEDIN_CLIENT_SECRET", channels: [Linkedin] },
];

export default function ClientConnections() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [status, setStatus] = useState(null);
  const [connections, setConnections] = useState([]);
  const [params, setParams] = useSearchParams();
  const confirm = useConfirm();
  const { user } = useAuth();
  const canConnect = ["admin", "ceo", "coo", "manager"].includes(user?.role);

  const load = async () => {
    try {
      const [{data: c}, {data: s}] = await Promise.all([
        api.get(`/clients/${id}`),
        api.get(`/clients/${id}/social/connections`),
      ]);
      setClient(c);
      setStatus(s.status);
      setConnections(s.connections || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
  };
  useEffect(()=>{ load(); }, [id]);

  useEffect(()=>{
    if (params.get("meta") === "connected_stub") toast.info("Meta OAuth reached — configure app secrets in backend/.env to persist tokens.");
    if (params.get("linkedin") === "connected_stub") toast.info("LinkedIn OAuth reached — configure client secret to persist tokens.");
  }, [params]);

  const connect = async (provider) => {
    const name = provider === "meta" ? "Meta (Instagram + Facebook)" : "LinkedIn";
    if (!canConnect) { toast.error(`Only an Admin can connect ${name}. Ask your admin to connect this client's account.`); return; }
    if (!status?.[provider]?.configured) { toast.error(`${name} app credentials have not been added yet. The ${name} App ID and Secret must be added to the app's keys before connecting.`); return; }
    try {
      const { data } = await api.get(`/social/${provider}/start`, { params: { client_id: id }});
      window.location.href = data.authorize_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const disconnect = async (conn) => {
    if (!(await confirm({ title: `Disconnect ${conn.provider === "meta" ? "Meta" : "LinkedIn"}?`, description: "Live stats will stop syncing for this client until you reconnect.", confirmText: "Disconnect" }))) return;
    await api.delete(`/social/connections/${conn.id}`);
    toast.success("Disconnected"); load();
  };

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1300px]">
      <Link to={`/clients/${id}`} className="inline-flex items-center gap-2 text-xs font-mono uppercase text-neutral-600 hover:text-neutral-800"><ArrowLeft className="w-3 h-3"/> Back to Client</Link>
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-orange-400">Social Integrations</div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight mt-1">Connections</h1>
        <p className="text-neutral-600 text-sm mt-1">{client?.client_name || "…"} · connect this client's social accounts so we can publish + pull analytics natively.</p>
      </div>

      <SocialConnectOptions clientId={id} clientEmail={client?.email} canConnect={canConnect} key={client?.email || "x"} />

      <div className="grid md:grid-cols-2 gap-4">
        {PROVIDERS.map((p) => {
          const Icon = p.icon;
          const st = status?.[p.key];
          const configured = st?.configured;
          const conns = connections.filter(c => c.provider === p.key);
          return (
            <div key={p.key} className="panel p-6 space-y-4" data-testid={`conn-card-${p.key}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${p.tint} border grid place-items-center`}>
                    <Icon className="w-5 h-5"/>
                  </div>
                  <div>
                    <div className="text-lg font-bold tracking-tight">{p.label}</div>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-neutral-500">{p.tag}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {p.channels.map((C, i) => <C key={i} className="w-4 h-4 text-neutral-600"/>)}
                </div>
              </div>

              {configured ? (
                <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-700 bg-emerald-500/10 border border-emerald-500/30 rounded-md px-3 py-2" data-testid={`conn-status-${p.key}`}>
                  <CheckCircle2 className="w-3.5 h-3.5"/> Provider configured — ready for OAuth
                </div>
              ) : (
                <div className="flex items-start gap-2 text-[11px] font-mono text-amber-700 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2" data-testid={`conn-status-${p.key}`}>
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
                  <div>
                    <div>Not configured yet</div>
                    <div className="text-neutral-600 mt-0.5 normal-case">Set <span className="text-neutral-800">{p.envLabel}</span> in <span className="text-neutral-800">backend/.env</span>, add the redirect URI to the platform's developer app, then reconnect.</div>
                  </div>
                </div>
              )}

              {st?.redirect_uri && (
                <div className="text-[10px] font-mono text-neutral-500 break-all">
                  Redirect: <span className="text-neutral-700">{st.redirect_uri}</span>
                </div>
              )}

              <div className="flex gap-2">
                <Button onClick={()=>connect(p.key)} className={`flex-1 ${configured && canConnect ? "bg-orange-500 hover:bg-orange-600 text-white" : "bg-orange-50 hover:bg-orange-100 text-orange-800 border border-orange-300"}`} data-testid={`conn-connect-${p.key}`}>
                  <Plug className="w-4 h-4 mr-1"/> Connect {p.key === "meta" ? "Meta" : "LinkedIn"}
                </Button>
                {p.key === "meta" && <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer"><Button variant="outline" className="border-[#E4E7EC]"><ExternalLink className="w-4 h-4"/></Button></a>}
                {p.key === "linkedin" && <a href="https://www.linkedin.com/developers/apps" target="_blank" rel="noreferrer"><Button variant="outline" className="border-[#E4E7EC]"><ExternalLink className="w-4 h-4"/></Button></a>}
              </div>

              {conns.length > 0 && (
                <div className="pt-3 border-t border-[#E4E7EC] space-y-2">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">Connected accounts</div>
                  {conns.map(c => (
                    <div key={c.id} className="flex items-center justify-between rounded-md bg-[#FFFFFF] border border-[#E4E7EC] px-3 py-2" data-testid={`conn-item-${c.id}`}>
                      <div className="text-sm truncate">
                        {c.page_name || c.linkedin_member_id || "Account"}
                        {c.instagram_id && <span className="text-[10px] font-mono text-neutral-500 ml-2">IG {c.instagram_id}</span>}
                      </div>
                      <button onClick={()=>disconnect(c)} className="text-rose-400 hover:text-rose-700" data-testid={`conn-disconnect-${c.id}`}><Trash2 className="w-4 h-4"/></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="panel p-5">
        <div className="text-[11px] font-mono uppercase tracking-widest text-orange-400 mb-2">Setup checklist</div>
        <ol className="list-decimal list-inside space-y-1 text-sm text-neutral-700">
          <li>Create a Meta Business App at <a className="text-orange-700 hover:text-orange-700 underline" href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer">developers.facebook.com/apps</a>. Add products: <span className="font-mono">Facebook Login for Business, Instagram Graph API, Pages API</span>. Add the redirect URI shown above. Copy App ID + App Secret into <span className="font-mono">backend/.env</span> as <span className="font-mono">META_APP_ID</span> and <span className="font-mono">META_APP_SECRET</span>. Restart backend.</li>
          <li>Create a LinkedIn Marketing App at <a className="text-orange-700 hover:text-orange-700 underline" href="https://www.linkedin.com/developers/apps" target="_blank" rel="noreferrer">linkedin.com/developers</a>. Request scopes: <span className="font-mono">w_organization_social, r_organization_social, r_organization_admin</span>. Add the redirect URI. Copy Client ID + Client Secret to <span className="font-mono">LINKEDIN_CLIENT_ID</span> / <span className="font-mono">LINKEDIN_CLIENT_SECRET</span> in <span className="font-mono">backend/.env</span>.</li>
          <li>Once both apps are approved by their respective review teams, the Connect buttons here will launch the real OAuth flow and start publishing + pulling analytics automatically.</li>
        </ol>
      </div>
    </div>
  );
}
